/**
 * Second Opinion — the learner's side (X-OBSERVER-001).
 *
 * Three jobs on one screen: ask someone, see who hasn't replied, and approve
 * what came back item by item. The approval gate is the whole point of the
 * feature: an observation that has not been ticked is inert — never scored,
 * never shown, never counted.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Check, Clock, History, Loader2, RotateCcw, Send, ShieldCheck, Trash2, Undo2, X,
} from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import {
  canResend, deleteObserverInvite, inviteStatusView, isExpired, isPending,
  listObserverInvites, needsReview, resendObserverInvite, saveObserverApproval,
  saveObserverDraft, sendObserverInvite, type ObserverInvite,
} from "@/lib/observerInvites";
import {
  EDIT_ACTION_LABEL, OBSERVER_ITEM_WEIGHT, OBSERVER_MAX_OPEN_INVITES,
  OBSERVER_PROMPTS, OBSERVER_RELATIONSHIPS, OBSERVER_TOTAL_CAP,
  approvalFromDraft, draftIsEdited, draftItemCount, editNoteInDraft, emptyApproval,
  hydrateDraft, observerAskCopy, relationshipLabel, relationshipNoun,
  restoreToDraft, retractFromDraft,
  type ObserverDraft, type ObserverEditEvent, type ObserverRelationship,
} from "@/features/assessment-v2/observer";
import { writeApprovedObserverInput } from "@/features/assessment-v2/observerStore";
import { PASSIONS } from "@/data/passions";
import { DEFAULT_BAND, parseBand } from "@/features/assessment-v2/band";

const labelFor = (id: string) => PASSIONS.find((p) => p.id === id)?.label ?? id;

function useBand() {
  return useMemo(() => {
    try {
      const stored = localStorage.getItem("wcc360.v2.band");
      return parseBand(stored ?? undefined) ?? DEFAULT_BAND;
    } catch { return DEFAULT_BAND; }
  }, []);
}

// ---- ask ----------------------------------------------------------------

function AskCard({ onSent, openCount }: { onSent: () => void; openCount: number }) {
  const band = useBand();
  const copy = observerAskCopy(band);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [relationship, setRelationship] = useState<ObserverRelationship>("parent");
  const [busy, setBusy] = useState(false);
  const atLimit = openCount >= OBSERVER_MAX_OPEN_INVITES;

  async function send() {
    setBusy(true);
    const res = await sendObserverInvite({
      observer_email: email.trim(),
      observer_name: name.trim() || undefined,
      relationship,
      band,
    });
    setBusy(false);
    if (res.ok === false) {
      toast({ title: "Not sent", description: res.error, variant: "destructive" });
      return;
    }
    setEmail(""); setName("");
    toast({
      title: "Asked",
      description: "They'll get a short form. You'll see it here the moment they reply.",
    });
    onSent();
  }

  return (
    <section className="rounded-3xl border border-border bg-card p-6" aria-labelledby="ask-heading">
      <h2 id="ask-heading" className="text-2xl font-semibold">{copy.title}</h2>
      <p className="mt-2 text-muted-foreground">{copy.body}</p>

      <fieldset className="mt-6">
        <legend className="mb-3 text-sm font-medium">Who are you asking?</legend>
        <div className="grid gap-2 sm:grid-cols-2">
          {OBSERVER_RELATIONSHIPS.map((r) => (
            <button
              key={r.id}
              type="button"
              aria-pressed={relationship === r.id}
              onClick={() => setRelationship(r.id)}
              className={[
                "min-h-[48px] rounded-2xl border px-4 py-3 text-left transition",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                relationship === r.id
                  ? "border-primary bg-primary/15"
                  : "border-border hover:border-primary/50",
              ].join(" ")}
            >
              <span className="block text-sm font-medium">{r.label}</span>
              <span className="block text-xs text-muted-foreground">{r.hint}</span>
            </button>
          ))}
        </div>
      </fieldset>

      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="observer-name">Their name</Label>
          <Input
            id="observer-name" value={name} className="min-h-[48px]"
            onChange={(e) => setName(e.target.value.slice(0, 80))}
            placeholder="Optional"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="observer-email">Their email</Label>
          <Input
            id="observer-email" type="email" value={email} className="min-h-[48px]"
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@example.com"
          />
        </div>
      </div>

      <Button
        onClick={send}
        disabled={busy || atLimit || !email.includes("@")}
        className="mt-5 min-h-[48px] w-full rounded-2xl sm:w-auto"
      >
        {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
        Ask them
      </Button>
      {atLimit ? (
        <p className="mt-3 text-sm text-muted-foreground">
          You've got {OBSERVER_MAX_OPEN_INVITES} requests open. Wait for one to come back, or remove one below.
        </p>
      ) : null}
      <p className="mt-4 flex gap-2 text-xs text-muted-foreground">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
        They never see your answers or your results. Nothing they send counts
        until you tick it — and what you tick can only add work to your list,
        never take any away.
      </p>
    </section>
  );
}

// ---- review -------------------------------------------------------------

/**
 * Review is editing, not voting.
 *
 * A parent can be half-right — right that you draw, wrong about why — and a
 * plain yes/no throws away the true half. So the learner works on their own
 * copy: take anything out, put it back, say it in their own words. The
 * observation itself is never overwritten, so "what they said" and "what I
 * kept" stay separately auditable, and nothing here is scored until approval.
 */
function ReviewCard({
  invite, onDone,
}: { invite: ObserverInvite; onDone: () => void }) {
  const obs = invite.observation!;
  const [draft, setDraft] = useState<ObserverDraft>(() => hydrateDraft(obs, invite.draft));
  const [history, setHistory] = useState<ObserverEditEvent[]>(
    () => invite.edit_history ?? [{
      at: invite.submitted_at ?? invite.created_at,
      action: "received",
      detail: `${obs.passion_ids.length + obs.cause_ids.length} things they noticed`,
    }],
  );
  const [showHistory, setShowHistory] = useState(false);
  const [busy, setBusy] = useState(false);
  const noun = relationshipNoun(invite.relationship);
  const from = invite.observer_name?.trim() || relationshipLabel(invite.relationship);

  const retracted = [...draft.retracted_cause_ids, ...draft.retracted_passion_ids];
  const kept = draftItemCount(draft);

  /** One write for the working copy and its history — they must not diverge. */
  const commit = useCallback((next: ObserverDraft, event: ObserverEditEvent) => {
    setDraft(next);
    setHistory((h) => {
      const nextHistory = [...h, event];
      void saveObserverDraft(invite.id, next, nextHistory);
      return nextHistory;
    });
  }, [invite.id]);

  const retract = (id: string) =>
    commit(retractFromDraft(draft, id), {
      at: new Date().toISOString(), action: "retracted", target: id, label: labelFor(id),
    });

  const restore = (id: string) =>
    commit(restoreToDraft(draft, id), {
      at: new Date().toISOString(), action: "restored", target: id, label: labelFor(id),
    });

  async function decide(outcome: "approved" | "declined") {
    setBusy(true);
    const approval = outcome === "approved"
      ? approvalFromDraft(obs, draft)
      : emptyApproval(invite.relationship, invite.observer_name ?? undefined);
    const finalHistory = [...history, {
      at: new Date().toISOString(),
      action: outcome === "approved" ? "approved" as const : "declined" as const,
      detail: outcome === "approved"
        ? `${kept} kept, ${retracted.length} taken out`
        : undefined,
    }];
    await saveObserverDraft(invite.id, draft, finalHistory);
    const ok = await saveObserverApproval(invite.id, approval, outcome);
    setBusy(false);
    if (!ok) {
      toast({ title: "Didn't save", description: "Try that again in a moment.", variant: "destructive" });
      return;
    }
    if (outcome === "approved" && (approval.passion_ids.length || approval.cause_ids.length)) {
      writeApprovedObserverInput(approval);
      toast({
        title: "Added to your picture",
        description: "Your report shows exactly what it changed, and by how much.",
      });
    } else {
      toast({ title: "Set aside", description: "None of it counts. Your results are unchanged." });
    }
    onDone();
  }

  return (
    <section className="rounded-3xl border border-primary/40 bg-card p-6" aria-labelledby={`review-${invite.id}`}>
      <p className="text-sm font-medium text-primary">Waiting for your call</p>
      <h2 id={`review-${invite.id}`} className="mt-1 text-2xl font-semibold">
        What {from} noticed
      </h2>
      <p className="mt-2 text-muted-foreground">
        Change any of it before you approve it. Take out what isn't you, put it
        back if you change your mind, say it in your own words. {noun} is never
        told what you kept, and nothing counts until you approve it.
      </p>
      <p className="mt-2 text-xs text-muted-foreground">
        Whatever you keep is worth {OBSERVER_ITEM_WEIGHT} of one of your own
        answers, capped at {OBSERVER_TOTAL_CAP} altogether — it can only widen
        what you see, never rule anything out.
      </p>

      {OBSERVER_PROMPTS.some((p) => obs.notes?.[p.id]) ? (
        <div className="mt-6 space-y-4">
          {OBSERVER_PROMPTS.filter((p) => obs.notes?.[p.id]).map((p) => {
            const value = draft.notes[p.id] ?? "";
            const changed = draft.edited_note_ids.includes(p.id);
            return (
              <div key={p.id} className="rounded-2xl border border-border p-4">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">{p.question}</p>
                <Label htmlFor={`note-${invite.id}-${p.id}`} className="mt-2 block text-sm">
                  In your words, if theirs aren't quite right
                </Label>
                <Textarea
                  id={`note-${invite.id}-${p.id}`}
                  value={value}
                  rows={3}
                  className="mt-2"
                  onChange={(e) => setDraft((d) => editNoteInDraft(d, p.id, e.target.value.slice(0, 600)))}
                  onBlur={() => commit(draft, {
                    at: new Date().toISOString(),
                    action: value.trim() ? "edited_note" : "cleared_note",
                    target: p.id,
                    label: p.question,
                  })}
                />
                <p className="mt-2 text-xs text-muted-foreground">
                  They wrote: “{obs.notes[p.id]}”
                  {changed && " · you've reworded this"}
                </p>
                {value.trim() ? (
                  <Button
                    variant="ghost" size="sm" className="mt-2 min-h-[44px]"
                    onClick={() => commit(editNoteInDraft(draft, p.id, ""), {
                      at: new Date().toISOString(), action: "cleared_note",
                      target: p.id, label: p.question,
                    })}
                  >
                    <X className="mr-2 h-4 w-4" /> Leave this out
                  </Button>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}

      {kept > 0 ? (
        <div className="mt-6">
          <h3 className="text-sm font-semibold">Staying in ({kept})</h3>
          <ul className="mt-2 grid gap-2 sm:grid-cols-2">
            {[...draft.cause_ids, ...draft.passion_ids].map((id) => (
              <li key={id} className="flex min-h-[48px] items-center gap-3 rounded-2xl border border-border px-4">
                <Check className="h-4 w-4 shrink-0 text-primary" aria-hidden />
                <span className="min-w-0 flex-1 truncate text-sm">{labelFor(id)}</span>
                <Button
                  variant="ghost" size="sm" className="min-h-[44px]"
                  onClick={() => retract(id)}
                  aria-label={`Take out ${labelFor(id)}`}
                >
                  <Undo2 className="mr-1 h-4 w-4" /> Take out
                </Button>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="mt-6 text-sm text-muted-foreground">
          You've taken all of it out. Put something back, or set the whole thing aside.
        </p>
      )}

      {retracted.length > 0 ? (
        <div className="mt-6">
          <h3 className="text-sm font-semibold">Taken out ({retracted.length})</h3>
          <p className="mt-1 text-xs text-muted-foreground">
            Nothing here counts. It stays visible so you can change your mind.
          </p>
          <ul className="mt-2 grid gap-2 sm:grid-cols-2">
            {retracted.map((id) => (
              <li key={id} className="flex min-h-[48px] items-center gap-3 rounded-2xl border border-dashed border-border px-4 text-muted-foreground">
                <span className="min-w-0 flex-1 truncate text-sm line-through">{labelFor(id)}</span>
                <Button
                  variant="ghost" size="sm" className="min-h-[44px]"
                  onClick={() => restore(id)}
                  aria-label={`Put back ${labelFor(id)}`}
                >
                  <RotateCcw className="mr-1 h-4 w-4" /> Put back
                </Button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="mt-6">
        <Button
          variant="ghost" size="sm" className="min-h-[44px]"
          aria-expanded={showHistory}
          onClick={() => setShowHistory((v) => !v)}
        >
          <History className="mr-2 h-4 w-4" />
          {showHistory ? "Hide what I changed" : `What I changed (${history.length})`}
        </Button>
        {showHistory ? (
          <ol className="mt-2 space-y-2 rounded-2xl bg-muted/40 p-4 text-sm">
            {history.map((e, i) => (
              <li key={`${e.at}-${i}`} className="text-muted-foreground">
                <span className="text-foreground">{EDIT_ACTION_LABEL[e.action]}</span>
                {e.label ? ` — ${e.label}` : ""}
                {e.detail ? ` — ${e.detail}` : ""}
                <span className="block text-xs">
                  {new Date(e.at).toLocaleString()}
                </span>
              </li>
            ))}
          </ol>
        ) : null}
      </div>

      <div className="mt-7 flex flex-col gap-3 sm:flex-row">
        <Button
          onClick={() => decide("approved")}
          disabled={busy || kept === 0}
          className="min-h-[48px] flex-1 rounded-2xl"
        >
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />}
          Approve {kept} thing{kept === 1 ? "" : "s"}
          {draftIsEdited(obs, draft) ? " as I've edited them" : ""}
        </Button>
        <Button
          onClick={() => decide("declined")}
          disabled={busy}
          variant="outline"
          className="min-h-[48px] flex-1 rounded-2xl"
        >
          <X className="mr-2 h-4 w-4" />
          None of this is me
        </Button>
      </div>
    </section>
  );
}

// ---- invite status ------------------------------------------------------

/**
 * Every request, in one place, with the state said in plain words. A learner
 * who asked three people deserves to know which of them the ball is with —
 * and to nudge, without generating a second live link to their observation.
 */
function InviteStatusRow({
  invite, onChanged, onRemove,
}: { invite: ObserverInvite; onChanged: () => void; onRemove: () => void }) {
  const [busy, setBusy] = useState(false);
  const view = inviteStatusView(invite);
  const who = invite.observer_name || invite.observer_email;

  async function resend() {
    setBusy(true);
    const res = await resendObserverInvite(invite.id);
    setBusy(false);
    if (res.ok === false) {
      toast({ title: "Not sent", description: res.error, variant: "destructive" });
      return;
    }
    toast({
      title: "Sent again",
      description: res.emailSent
        ? "Same link, another 30 days. You'll see their reply here."
        : "We couldn't email them just now. Try again in a few minutes.",
    });
    onChanged();
  }

  const Icon = view.key === "approved" ? Check
    : view.key === "declined" ? X
    : view.key === "expired" ? Clock
    : Clock;

  return (
    <li className="rounded-2xl border border-border p-3">
      <div className="flex items-start gap-3">
        <Icon
          className={cn(
            "mt-0.5 h-4 w-4 shrink-0",
            view.key === "approved" ? "text-primary" : "text-muted-foreground",
          )}
          aria-hidden
        />
        <div className="min-w-0 flex-1">
          <p className="flex flex-wrap items-center gap-2">
            <span className="truncate font-medium">{who}</span>
            <span className="rounded-full border border-border px-2 py-0.5 text-xs text-muted-foreground">
              {view.label}
            </span>
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            {relationshipLabel(invite.relationship)} · asked{" "}
            {new Date(invite.created_at).toLocaleDateString()}
            {invite.resend_count > 0 &&
              ` · nudged ${invite.resend_count} time${invite.resend_count === 1 ? "" : "s"}`}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">{view.note}</p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {canResend(invite) ? (
            <Button
              variant="ghost" size="sm" className="min-h-[44px]"
              disabled={busy || invite.resend_count >= 3}
              onClick={resend}
            >
              {busy
                ? <Loader2 className="mr-1 h-4 w-4 animate-spin" aria-hidden />
                : <Send className="mr-1 h-4 w-4" aria-hidden />}
              {isExpired(invite) ? "Send again" : "Nudge"}
            </Button>
          ) : null}
          <Button
            variant="ghost" size="sm" className="min-h-[44px]"
            onClick={onRemove}
            aria-label={`Remove request to ${who}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </li>
  );
}

// ---- page ---------------------------------------------------------------

export default function SecondOpinion() {
  const [invites, setInvites] = useState<ObserverInvite[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setInvites(await listObserverInvites());
    setLoading(false);
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const open = invites.filter((i) => isPending(i) || isExpired(i));
  const toReview = invites.filter(needsReview);
  const settled = invites.filter((i) => i.status === "approved" || i.status === "declined");

  async function remove(id: string) {
    if (!(await deleteObserverInvite(id))) return;
    const wasApproved = invites.find((i) => i.id === id)?.status === "approved";
    if (wasApproved) writeApprovedObserverInput(null);
    void refresh();
  }

  return (
    <main className="mx-auto max-w-3xl px-5 pb-24 pt-10">
      <header className="mb-8">
        <p className="text-sm font-medium text-primary">Career Clarity 360</p>
        <h1 className="mt-2 text-3xl font-semibold leading-tight">A second opinion</h1>
        <p className="mt-3 max-w-prose text-muted-foreground">
          Someone who has watched you for years sees patterns you're too close
          to notice. Ask them what they've seen — then edit it, keep what's
          true, and drop what isn't. You always have the last word.
        </p>
      </header>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-label="Loading" />
        </div>
      ) : (
        <div className="space-y-6">
          {toReview.map((i) => (
            <ReviewCard key={i.id} invite={i} onDone={refresh} />
          ))}

          <AskCard onSent={refresh} openCount={open.filter(isPending).length + toReview.length} />

          {open.length > 0 ? (
            <section className="rounded-3xl border border-border bg-card p-6" aria-labelledby="waiting-heading">
              <h2 id="waiting-heading" className="text-lg font-semibold">Still with them</h2>
              <ul className="mt-3 space-y-2">
                {open.map((i) => (
                  <InviteStatusRow
                    key={i.id} invite={i}
                    onChanged={refresh} onRemove={() => remove(i.id)}
                  />
                ))}
              </ul>
            </section>
          ) : null}

          {settled.length > 0 ? (
            <section className="rounded-3xl border border-border bg-card p-6" aria-labelledby="settled-heading">
              <h2 id="settled-heading" className="text-lg font-semibold">Already decided</h2>
              <ul className="mt-3 space-y-2">
                {settled.map((i) => (
                  <InviteStatusRow
                    key={i.id} invite={i}
                    onChanged={refresh} onRemove={() => remove(i.id)}
                  />
                ))}
              </ul>
            </section>
          ) : null}

          <p className="text-center text-sm text-muted-foreground">
            <Link to="/careers" className="underline underline-offset-4">
              See how it changed your matches
            </Link>
          </p>
        </div>
      )}
    </main>
  );
}
